
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    
import style from '../styles/form.module.css'

import { getLayout, LayoutContext } from '../components/sg/Layout'
import Form, { Step } from '../components/Form'
import useTranslation from 'next-translate/useTranslation'
import classNames from 'classnames/bind'
import { useState, useContext, useRef, useEffect } from 'react'
import Router, { useRouter } from 'next/router'
import { countries, getSortedCountries } from '../helpers/countries'
import Link from 'next/link'
import useAuth from '../hooks/useAuth'
import PasswordScore, { scorePassword } from '../components/PasswordScore'
import myInfo from '../helpers/myinfo'
import dateOfBirth from '../helpers/dob'
import { event } from '../helpers/analytics'
import { nationalities } from '../helpers/nationalities'
import useLocalStorageState from 'use-local-storage-state'
import { currencies, defaultCurrency } from '../hooks/usePrices'
import DateSelect from '../components/DateSelect'
import IDUpload from '../components/IDUpload'
import useUser from '../hooks/useUser'
import enums from '../data/enums.json'

const defaultCountry = 'SG'
const countriesList = getSortedCountries()
const referralCodes = enums.ReferralLists

// Server side prep for Myinfo response
 async function _getServerSideProps ({ query: { code, state }, req: { headers: { host, 'x-vercel-ip-country': ipCountry } }, res }) {
  const myInfoTimeout = 4 * 60 * 1000
  ipCountry = ipCountry || defaultCountry
  const defaultPrefill = {
    nationality: ipCountry,
    uinfin: '',
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    street: '',
    street2: '',
    city: '',
    state: '',
    postalCode: '',
    country: ipCountry,
    gender: '',
    birthDate: '',
    residentialStatus: '',
    currency: currencies.find(c => !c.countries || c.countries.includes(ipCountry))?.code || defaultCurrency,
    timestamp: '',
    source: ''
  }
  if (state?.indexOf('myinfo_1') === 0 && code) {
    res.setHeader('Cache-Control', 'no-store')
    try {
      const requestTime = parseInt(state.substr(8), 10)
      if (requestTime > new Date().getTime() - myInfoTimeout) {
        const url = (host.match(/^localhost/) ? 'http://' : 'https://') + host
        const prefill = await myInfo(code, url, defaultPrefill)
        return { props: { prefill } }
      } else {
        throw new Error('Timeout')
      }
    } catch (error) {
      return { props: { prefill: Object.assign(defaultPrefill, { error: error.message }) } }
    }
  } else {
    return { props: { prefill: defaultPrefill } }
  }
}

export default function Signup ({ prefill }) {
  const router = useRouter()
  const signupForms = {
    personal: useRef(),
    corporate: useRef(),
    twoFa: useRef(),
    other: useRef()
  }
  const accountTypeElem = useRef()
  const nationalityElem = useRef()
  const countryElem = useRef()
  const currencyElem = useRef()
  const referredByElem = useRef()
  const passwordElem = useRef()
  const passwordRepeatElem = useRef()
  const twofaCodeElem = useRef()
  const { t } = useTranslation('main')
  const { locale } = router
  const c = classNames.bind(style)
  const { isAuthed, setAuth, authedFetch, setTwoFa } = useAuth()
  // eslint-disable-next-line no-unused-vars
  const [layoutContext, setLayoutContext] = useContext(LayoutContext)
  const [passwordVisible, setPasswordVisible] = useState(false)
  const [currentStep, setCurrentStep] = useState(!prefill?.source ? 'anim' : 'personal')
  const [passwordScore, setPasswordScore] = useState(0)
  const [twoFaRetry, setTwoFaRetry] = useState()
  // const [userNo, setUserNo] = useState(1)
  const [referralCode, setReferralCode] = useLocalStorageState('referralCode')
  const [birthDate, setBirthDate] = useState('')
  const { user, account } = useUser()

  useEffect(() => {
    setReferralCode(router.query.referralCode || '')
  }, [router])

  useEffect(() => {
    if (!prefill.source) {
      setTimeout(() => { setCurrentStep('intro') }, 3200)
    }
    if (prefill.error) {
      console.warn(prefill.error)
      setLayoutContext({
        message: prefill.error === 'Timeout'
          ? t('signup-myinfo-timeout')
          : (
            <>
              {t('signup-myinfo-notavailable')}
              <br /><br />
              {t('signup-myinfo-notavailable-contact')}
            </>
          ),
        timeout: 15000
      })
    }
    window.history.replaceState(null, document.title, window.location.origin + '/signup')
  }, [])

  useEffect(() => {
    if (user) {
      window.sessionStorage.setItem('email_' + user.Id, new Date().toISOString())
      window.sessionStorage.setItem('id_' + account.AccountId, new Date().toISOString())
    }
  }, [user])

  useEffect(() => {
    if (isAuthed && ['anim', 'intro'].includes(currentStep)) {
      Router.push('/portfolio')
    }
  }, [isAuthed])

  const handleSignupSubmit = async e => {
    e.preventDefault()
    setLayoutContext({})

    const signupFormElem = signupForms[accountTypeElem.current.value]
    signupFormElem.current.removeAttribute('novalidate')

    if (passwordScore < 5) {
      passwordElem.current.setCustomValidity(t('signup-passwordnotcompliant'))
    } else if (passwordElem.current.value !== passwordRepeatElem.current.value) {
      passwordRepeatElem.current.setCustomValidity(t('signup-passwordsdonotmatch'))
      passwordElem.current.setCustomValidity(t('signup-passwordsdonotmatch'))
    }

    if (signupFormElem.current.reportValidity()) {
      setCurrentStep('loading')
      const body = Object.assign(prefill, {
        countryIso: countryElem?.current?.value || prefill.country,
        nationalityIso: nationalityElem?.current?.value || prefill.nationality,
        currency: currencyElem?.current?.value || prefill.currency,
        birthDate: birthDate || prefill.birthDate,
        referredBy: referredByElem?.current.value
      })
      signupFormElem.current.querySelectorAll('input').forEach(elem => {
        body[elem.id] = elem.value.trim()
      })
      const response = await window.fetch('/api/signup', {
        method: 'POST',
        mode: 'same-origin',
        cache: 'no-cache',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json'
        },
        redirect: 'error',
        referrerPolicy: 'no-referrer',
        body: JSON.stringify(body)
      })
      if (!response.ok) {
        setCurrentStep(accountTypeElem.current.value)
        setLayoutContext({ message: t('signup-unknownerror') })
      } else {
        const json = await response.json()
        if (setAuth(json)) {
          setCurrentStep('2fa')
          event('conversion', 'i5nBCLbS250ZEOLP0NID')
        } else if (json.validation_errors?.length) {
          setCurrentStep(accountTypeElem.current.value)
          let message
          json.validation_errors.forEach(e => {
            if (e.code === 'CodeErrJuris') {
              window.location = 'https://www.silverbullion.com.sg/info/high-risk-jurisdiction'
            } else if (e.field) {
              // TODO: i18n map message to e.code
              const field = e.field[0].toLowerCase() + e.field.substr(1)
              const elem = document.getElementById(field)
              if (elem) {
                elem.setCustomValidity(e.message)
              } else {
                // TODO: i18n map message to e.code
                message = e.message
              }
            } else {
              // TODO: i18n map message to e.code
              message = e.message
            }
          })
          if (message) {
            setLayoutContext({ message })
          }
          signupFormElem.current.reportValidity()
          setTimeout(() => signupFormElem.current.reportValidity(), 300)
        } else if (json.login) {
          setLayoutContext({ success: t('signup-successno2fa') })
          Router.push('/login')
        } else {
          setCurrentStep(accountTypeElem.current.value)
          setLayoutContext({ message: t('signup-unknownerror') })
        }
      }
    }
  }

  const handle2faSubmit = async e => {
    e.preventDefault()
    setLayoutContext({})

    signupForms.twoFa.current.removeAttribute('novalidate')

    if (signupForms.twoFa.current.reportValidity()) {
      setCurrentStep('loading')
      const response = await authedFetch('/api/user/2fa/phone', {
        method: 'POST',
        cache: 'no-cache',
        body: JSON.stringify({
          token: twofaCodeElem.current.value.trim()
        })
      })
      if (!response.ok) {
        setCurrentStep('2fa')
        twofaCodeElem.current.setCustomValidity(t('login-2faerror'))
      } else {
        setCurrentStep('upload')
        setTwoFa(true)
        window.sessionStorage.setItem('id_' + account.AccountId, new Date().toISOString())
      }
    }
  }

  const handlePasswordVisibility = e => {
    e.preventDefault()
    setPasswordVisible(!passwordVisible)
  }

  const handle2faResend = async (phone, error = '') => {
    phone = window.prompt(error + t('signup-resendphone2fasetuptoken'), phone)
    if (phone !== null) {
      const response = await authedFetch('/api/user/2fa/phone/send', {
        method: 'POST',
        cache: 'no-cache',
        body: JSON.stringify({
          Phone: phone.trim()
        })
      })
      if (!response.ok) {
        handle2faResend(phone, t('signup-resendphone2fasetuptokenerror') + ' ')
      }
      setTimeout(() => setTwoFaRetry(true), 3000)
    }
  }

  const handleMyinfo = e => {
    e.preventDefault()
    const useDevelopment = !process.env.NEXT_PUBLIC_MYINFO_CLIENT_ID
    const useStaging = useDevelopment || process.env.NEXT_PUBLIC_MYINFO_CLIENT_ID.match(/^STG/)
    const myInfoClientId = useDevelopment ? 'STG2-MYINFO-SELF-TEST' : process.env.NEXT_PUBLIC_MYINFO_CLIENT_ID
    const myInfoApiUrl = useStaging ? 'https://sandbox.api.myinfo.gov.sg/com/v3' : 'https://api.myinfo.gov.sg/com/v3'
    window.location = myInfoApiUrl + '/authorise?client_id=' + myInfoClientId + '&purpose=' + encodeURIComponent(t('signup-personalform-myinfo-purpose')) +
      '&state=myinfo_1' + new Date().getTime().toString() +
      '&redirect_uri=' + encodeURIComponent(window.location.origin + '/callback') +
      '&attributes=uinfin,name,aliasname,sex,dob,residentialstatus,nationality,mobileno,email,regadd'
  }

  const accountLogin = (
    <>
      <h3>{t('signup-userform-title')}</h3>
      <div className={c('inputfield')}>
        <input type='text' id='username' required autoComplete='username' minLength={5} maxLength={20} pattern='^[A-Za-z0-9@_\-$]+$' placeholder={t('signup-username-placeholder')} onChange={e => { e.target.setCustomValidity('') }} />
        <div>
          {t('signup-passwordrequirements')}
          <br />
          {locale.startsWith('de') && <a href='https://www.verbraucherzentrale.de/wissen/digitale-welt/datenschutz/starke-passwoerter-so-gehts-11672' target='_blank' rel='noopener noreferrer nofollow'>{t('signup-passwordreadmore-link')}</a>}
          {!locale.startsWith('de') && <a href='https://www.howtogeek.com/195430/how-to-create-a-strong-password-and-remember-it/' target='_blank' rel='noopener noreferrer nofollow'>{t('signup-passwordreadmore-link')}</a>}
        </div>
      </div>
      <div className={c('inputfield')}>
        <input type={passwordVisible ? 'text' : 'password'} required ref={passwordElem} autoComplete='new-password' minLength={8} id='password' placeholder={t('signup-password-placeholder')} onChange={e => { e.target.setCustomValidity(''); passwordRepeatElem.current.setCustomValidity(''); setPasswordScore(scorePassword(e.target.value)) }} />
        <button type='button' className={c('revealpassword') + ' light'} onClick={handlePasswordVisibility}>
          {!passwordVisible && <img src='/images/reveal.svg' alt={t('signup-password-reveal')} />}
          {passwordVisible && <img src='/images/hide.svg' alt={t('signup-password-hide')} />}
        </button>
        <PasswordScore score={passwordScore} />
      </div>
      <div className={c('inputfield')}>
        <input type={passwordVisible ? 'text' : 'password'} required minLength={8} ref={passwordRepeatElem} id='passwordRepeat' placeholder={t('signup-passwordrepeat-placeholder')} onChange={e => { e.target.setCustomValidity(''); passwordElem.current.setCustomValidity('') }} />
      </div>
    </>
  )

  return (
    <Form
      title={t('signup-title')}
      onClose={currentStep === 'intro' ? () => Router.push('/sg') : (currentStep === 'upload' ? () => Router.push('/portfolio') : undefined)}
      onBack={!['intro', 'anim', 'loading', 'success', 'upload'].includes(currentStep) ? () => setCurrentStep('intro') : undefined}
      step={currentStep}
    >
      <Step name='anim'>
        <div className={c('anim')}>
          <img src='/images/gold_large.png' className={c('goldanim')} />
        </div>
      </Step>
      <Step name='intro'>
        <div className={c('intro')}>
          <h3>{t('signup-intro-title')}</h3>
          <p>
            {t('signup-intro-text1')}
          </p>
          <p className={c('subtext')}>
            {t('signup-intro-text2')}
          </p>
          <div className={c('inputfield')}>
            <label htmlFor='accountType'>{t('signup-accounttype-label')}</label>
            <select ref={accountTypeElem} id='accountType' defaultValue='personal'>
              <option value='personal'>{t('signup-accounttype-personal')}</option>
              <option value='joint'>{t('signup-accounttype-joint')}</option>
              <option value='corporate'>{t('signup-accounttype-corporate')}</option>
              <option value='other'>{t('signup-accounttype-other')}</option>
            </select>
          </div>
          <br />
          <button type='button' onClick={() => setCurrentStep(accountTypeElem.current.value)}>{t('signup-startbutton')}</button>
          <br />
          <br />
          <br />
          <img src='/images/logo-icon-black.svg' width={36} height={36} alt='Silver Bullion' style={{ float: 'left', margin: '0 1em 0 0' }} />
          <Link href='/login'>
            <a style={{ display: 'inline-block', width: '280px', color: '#666' }}>
              {t('signup-login-link')}
            </a>
          </Link>
          <br />
          {/* <p className={c('note')}>{t('signup-existingaccount-note')}</p> */}
        </div>
      </Step>
      <Step name='personal'>
        <form noValidate onSubmit={handleSignupSubmit} ref={signupForms.personal} className={c('personal')}>
          <img src='/_next/image?url=%2Fimages%2Fgold_large.png&w=384&q=75' style={{ float: 'right', marginLeft: '20px', width: '70px', borderRadius: '14px', boxShadow: '2px 2px 2px rgba(0,0,0,.4)' }} />
          <h3>{t('signup-personalform-title')}</h3>
          <p><b>{t('signup-personalform-promo')}</b></p>
          {!prefill.source ? (
            <>
              {prefill.country === 'SG' && (
                <>
                  <div className={c('inputfield')}>
                    <button onClick={handleMyinfo} className='light noborder'><img src='/images/myinfo.png' style={{ verticalAlign: '-150%' }} width={168} height={60} alt={t('signup-myinfo-button')} /></button>
                    <div>{t('signup-personalform-myinfo-description')}</div>
                  </div>
                  <br />
                </>
              )}
              <div className={c('inputfield')}>
                <label htmlFor='nationality'>{t('signup-nationality-label')}</label>
                <select id='nationality' ref={nationalityElem} defaultValue={prefill.nationality}>
                  {countriesList.map(country => <option key={country.code} value={country.code}>{country.name}</option>)}
                </select>
              </div>
              <div className={c('inputfield')}>
                <label htmlFor='firstName'>{t('signup-personaldetails-label')}</label>
                <div>{t('signup-personaldetails-notamailingaddress')}</div>
                <input type='text' className={c('half')} id='firstName' autoComplete='given-name' required placeholder={t('signup-firstname-placeholder')} onChange={e => { e.target.setCustomValidity('') }} />
                <input type='text' id='lastName' autoComplete='family-name' required placeholder={t('signup-lastname-placeholder')} onChange={e => { e.target.setCustomValidity('') }} />
              </div>
              <div className={c('inputfield')}>
                <input type='text' id='street' required autoComplete='address-line1' placeholder={t('signup-street-placeholder')} defaultValue={prefill.street} onChange={e => { e.target.setCustomValidity('') }} />
              </div>
              <div className={c('inputfield')}>
                <input type='text' id='street2' autoComplete='address-line2' placeholder={t('signup-street2-placeholder')} defaultValue={prefill.street2} onChange={e => { e.target.setCustomValidity('') }} />
              </div>
              <div className={c('inputfield')}>
                <input type='text' id='city' required autoComplete='address-city' placeholder={t('signup-city-placeholder')} defaultValue={prefill.city} onChange={e => { e.target.setCustomValidity('') }} />
              </div>
              <div className={c('inputfield')}>
                <input type='text' className={c('half')} id='postalCode' autoComplete='postal-code' required placeholder={t('signup-postalcode-placeholder')} defaultValue={prefill.postalCode} onChange={e => { e.target.setCustomValidity('') }} />
                <input type='text' id='state' autoComplete='address-state' placeholder={t('signup-state-placeholder')} defaultValue={prefill.state} />
              </div>
              <div className={c('inputfield')}>
                <select ref={countryElem} id='country' defaultValue={prefill.country} autoComplete='country'>
                  {countriesList.map(country => <option key={country.code} value={country.code}>{country.name}</option>)}
                </select>
              </div>
              <div className={c('inputfield')}>
                <label>{t('signup-birthdate')}</label>
                <DateSelect required onChange={e => { setBirthDate(e.target.value); e.target.setCustomValidity('') }} locale={locale} />
              </div>
            </>
          ) : (
            <>
              <br />
              <p>
                {t('verify-review-info')} <a href='https://myinfo.gov.sg' target='_blank' rel='noopener noreferrer'>https://myinfo.gov.sg</a>.
              </p>
              <div className={c('inputfield')}>
                <table className={c('table')} cellSpacing={0}>
                  <tbody>
                    <tr>
                      <td>{t('verify-nricfin-placeholder')}:</td>
                      <td>{prefill.uinfin}</td>
                    </tr>
                    <tr>
                      <td>{t('verify-name-placeholder')}:</td>
                      <td>{prefill.name}</td>
                    </tr>
                    {prefill.firstName ? (
                      <tr>
                        <td>{t('verify-alias-placeholder')}:</td>
                        <td>{prefill.firstName}</td>
                      </tr>
                    ) : null}
                    <tr>
                      <td>{t('verify-nationality-placeholder')}:</td>
                      <td>{nationalities[prefill.nationality] || prefill.nationality}</td>
                    </tr>
                    {prefill.residentialStatus ? (
                      <tr>
                        <td>{t('verify-residentialstatus-placeholder')}:</td>
                        <td>{prefill.residentialStatus === 'C' ? t('verify-residentialstatus-citizen') : (prefill.residentialStatus === 'P' ? t('verify-residentialstatus-pr') : prefill.residentialStatus)}</td>
                      </tr>
                    ) : null}
                    <tr>
                      <td>{t('verify-gender-placeholder')}:</td>
                      <td>{prefill.gender === 'M' ? t('verify-gender-male') : (prefill.gender === 'F' ? t('verify-gender-female') : (prefill.gender || t('verify-gender-unspecified')))}</td>
                    </tr>
                    {prefill.birthDate ? (
                      <tr>
                        <td>{t('verify-dateofbirth-placeholder')}:</td>
                        <td>{dateOfBirth(prefill.birthDate, locale)}</td>
                      </tr>
                    ) : null}
                    <tr>
                      <td>{t('verify-address-placeholder')}:</td>
                      <td>{[prefill.street, prefill.street2, prefill.city + ' ' + prefill.postalCode, prefill.state, countries.getName(prefill.country)].map(t => t.trim()).filter(t => !!t).join(', ')}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <br />
              {(!prefill.street || !prefill.postalCode) && (
                <>
                  <div className={c('inputfield')}>
                    <label htmlFor='firstName'>{t('signup-addressin-label')} {countries.getName(prefill.country)}</label>
                    <input type='text' id='street' required autoComplete='address-line1' placeholder={t('signup-streethousenumber-placeholder')} onChange={e => { e.target.setCustomValidity('') }} />
                  </div>
                  <div className={c('inputfield')}>
                    <input type='text' id='street2' autoComplete='address-line2' placeholder={t('signup-floorunit-placeholder')} onChange={e => { e.target.setCustomValidity('') }} />
                  </div>
                  <div className={c('inputfield')}>
                    <input type='text' id='postalCode' autoComplete='postal-code' required placeholder={t('signup-postalcode-placeholder')} onChange={e => { e.target.setCustomValidity('') }} />
                  </div>
                  {prefill.country === 'SG' ? <input type='hidden' id='city' defaultValue='Singapore' /> : (
                    <div className={c('inputfield')}>
                      <input type='text' id='city' required autoComplete='address-city' className={c('half')} placeholder={t('signup-city-placeholder')} onChange={e => { e.target.setCustomValidity('') }} />
                      <input type='text' id='state' autoComplete='address-state' placeholder={t('signup-state-placeholder')} />
                    </div>
                  )}
                  <br />
                </>
              )}
            </>
          )}
          <div className={c('inputfield')}>
            <label htmlFor='email'>{t('signup-contact-label')}</label>
            <input type='email' inputMode='email' id='email' autoComplete='email' required placeholder={t('signup-email-placeholder')} defaultValue={prefill.email} onChange={e => { e.target.setCustomValidity('') }} />
          </div>
          <div className={c('inputfield')}>
            <input type='tel' inputMode='tel' id='phone' required autoComplete='tel' placeholder={t('signup-phone-placeholder')} defaultValue={prefill.phone} onChange={e => { e.target.setCustomValidity('') }} />
            <div>{t('signup-phone-info')}</div>
          </div>
          <div className={c('inputfield')}>
            <label htmlFor='currency'>{t('signup-currency-label')}</label>
            <select ref={currencyElem} id='currency' defaultValue={prefill.currency} autoComplete='currency'>
              {currencies.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
            </select>
          </div>
          <br />
          {(!accountTypeElem.current || accountTypeElem.current.value === 'personal') && accountLogin}
          <br />
          <h3>{t('signup-referralcode-header')}</h3>
          <div className={c('inputfield')}>
            <select ref={referredByElem} defaultValue='' id='referredBy' required>
              <option disabled value=''>{t('signup-referredby-placeholder')}</option>
              {referralCodes.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className={c('inputfield')}>
            <input type='text' id='referralCode' placeholder={t('signup-referralcode-placeholder')} defaultValue={referralCode} key={referralCode} />
          </div>
          <br />
          <p className={c('subtext')}>
            {t('signup-acceptingterms')}
            <br />
            <a href='https://www.silverbullion.com.sg/TermsOfService/STARGrams' target='_blank' rel='noopener noreferrer'>{t('signup-terms')}</a> &amp; <a href='https://www.silverbullion.com.sg/privacy' target='_blank' rel='noopener noreferrer'>{t('signup-privacy')}</a>.
          </p>
          <br />
          <button className='pulsate' type='submit'>{t('signup-continuebutton')}</button>
          <br />
          <br />
          <br />
        </form>
      </Step>
      <Step name='joint'>
        <h3>{t('signup-jointform-title')}</h3>
        <p>Please follow the link below to begin the process of opening your account directly with Silver Bullion.</p>
        <a href='https://www.silverbullion.com.sg/Register?' rel='noopener noreferrer'>Go to https://silverbullion.com.sg</a>
      </Step>
      <Step name='corporate'>
        <h3>{t('signup-corporateform-title')}</h3>
        <p>Please follow the link below to begin the process of opening your account directly with Silver Bullion.</p>
        <a href='https://www.silverbullion.com.sg/Register?' rel='noopener noreferrer'>Go to https://silverbullion.com.sg</a>
      </Step>
      <Step name='other'>
        <h3>{t('signup-corporateform-title')}</h3>
        <p>Please follow the link below to begin the process of opening your account directly with Silver Bullion.</p>
        <a href='https://www.silverbullion.com.sg/Register?' rel='noopener noreferrer'>Go to https://silverbullion.com.sg</a>
      </Step>
      <Step name='2fa'>
        <form noValidate ref={signupForms.twoFa} id='2faform' onSubmit={handle2faSubmit} className={c('twofa')}>
          <h3>{t('signup-2faviaphonesetup-title')}</h3>
          <p>{t('signup-2faviaphonesetup-info')}</p>
          <div className={c('inputfield')}>
            <input type='text' ref={twofaCodeElem} id='2facode' autoComplete='one-time-code' required minLength={6} maxLength={6} pattern='[0-9]*' inputMode='numeric' placeholder={t('signup-2facode-placeholder')} onChange={e => e.target.setCustomValidity('')} />
            <div><a href='#' onClick={e => { e.preventDefault(); handle2faResend() }}>{t('signup-changephonenumber-button')}</a></div>
          </div>
          <br />
          <button type='submit'>{t('signup-2fa-verifybutton')}</button>
          <br /><br />
          {twoFaRetry && (
            <p className={c('note')}>
              <b>{t('signup-2faavailabilitynote-title')}:</b> {t('signup-2faavailabilitynote')}
            </p>
          )}
        </form>
      </Step>
      <Step name='upload'>
        <h3>{t('signup-form-header')}</h3>
        <p>{t('signup-description')}</p>
        <IDUpload onSubmit={() => setCurrentStep('loading')} onError={() => setCurrentStep('upload')} onSuccess={() => setCurrentStep('success')} />
      </Step>
      <Step name='loading'>
        <div className={c('loading')}>
          <img src='/images/loader.svg' />
        </div>
      </Step>
      <Step name='success'>
        <div className={c('leadingsuccess', { success: currentStep === 'success' })}>
          <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 70 70'>
            <path fill='#D8D8D8' d='M35,60 C21.1928813,60 10,48.8071187 10,35 C10,21.1928813 21.1928813,10 35,10 C48.8071187,10 60,21.1928813 60,35 C60,48.8071187 48.8071187,60 35,60 Z M23.6332378,33.2260427 L22.3667622,34.7739573 L34.1433655,44.40936 L47.776114,27.6305926 L46.223886,26.3694074 L33.8566345,41.59064 L23.6332378,33.2260427 Z' />
            <circle stroke='#F6C358' cx='35' cy='35' r='24' strokeWidth='1.3' strokeLinecap='round' fill='transparent' />
            <polyline strong='#F6C358' strokeWidth='1.3' points='23 34 34 43 47 27' fill='transparent' />
          </svg>
        </div>
        <div>
          <h3>{t('signup-success-title')}</h3>
          <p>{t('signup-success-description')}</p>
          <p className={c('subtext')}>{t('signup-success-nextstep')}</p>
          <img src='/images/gold3d.svg' style={{ float: 'right', width: '100px' }} />
          <br />
          <Link href='/sg/tour'>
            <button>{t('signup-buygoldnowbutton')}</button>
          </Link>
          <br />
          <br />
          <a href='/upload'>{t('signup-fund-link')}</a>
          <br /><br />
        </div>
      </Step>
    </Form>
  )
}

Signup.getLayout = getLayout


    export async function getServerSideProps(ctx) {
        let res = _getServerSideProps(ctx)
        if(typeof res.then === 'function') res = await res
        return {
          
          ...res,
          props: {
            ...(res.props || {}),
            ...(await __loadNamespaces({
              ...ctx,
              pathname: '/signup',
              loaderName: 'getServerSideProps',
              ...__i18nConfig,
              loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
            }))
          }
        }
    }
  